/**
 * GlobalNotificationHandler Component
 *
 * This component handles global WebSocket notification events and displays
 * appropriate UI components based on the message type:
 * - notification: Shows a toast notification
 * - warning: Shows a dismissable warning modal
 * - logout: Shows a force logout modal with countdown
 *
 * It also checks for force logout flags on mount to prevent users from
 * bypassing logout by refreshing the page.
 */

import { useState, useEffect, useCallback } from "react";
import { useWebSocketEvent } from "../services/websocket/useWebSocketEvent";
import {
  NotificationEventData,
  WarningEventData,
  LogoutEventData,
} from "../services/websocket/connectionManager";
import MaintenanceWarning from "./MaintenanceWarning";
import ForceLogoutModal, { FORCE_LOGOUT_FLAG } from "./ForceLogoutModal";
import Toast from "./Toast";

interface NotificationToast {
  id: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
}

export const GlobalNotificationHandler = () => {
  // Warning modal state
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Logout modal state
  const [showLogout, setShowLogout] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState("");

  // Toast notifications state
  const [toasts, setToasts] = useState<NotificationToast[]>([]);

  // Add a toast notification
  const addToast = useCallback(
    (message: string, type: NotificationToast["type"] = "info") => {
      const id = `toast-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      // Clear all previous toasts when a new one arrives, since we only display one at a time
      // This prevents old toasts from reappearing after the new one dismisses
      setToasts([{ id, message, type }]);
      // Note: Toast component handles auto-dismiss with proper animation
      // No need for external setTimeout here
    },
    []
  );

  // Remove a specific toast
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Check for force logout flag on mount (page reload protection)
  useEffect(() => {
    const logoutFlag = sessionStorage.getItem(FORCE_LOGOUT_FLAG);
    if (logoutFlag === "true") {
      console.debug(
        "[GlobalNotificationHandler] Force logout flag detected on mount"
      );
      // Show the logout modal immediately
      setLogoutMessage(
        "Your session was terminated. You are being logged out."
      );
      setShowLogout(true);
    }
  }, []);

  // Handle notification events (info toasts)
  useWebSocketEvent<NotificationEventData>("notification", (data) => {
    console.debug("[GlobalNotificationHandler] Received notification:", data);
    addToast(data.message || "You have a new notification", "info");
  });

  // Handle warning events (dismissable modal)
  useWebSocketEvent<WarningEventData>("warning", (data) => {
    console.debug("[GlobalNotificationHandler] Received warning:", data);
    setWarningMessage(data.message || "An important warning has been issued.");
    setShowWarning(true);
  });

  // Handle logout events (force logout)
  useWebSocketEvent<LogoutEventData>("logout", (data) => {
    console.debug("[GlobalNotificationHandler] Received logout event:", data);
    setLogoutMessage(
      data.message || "You are being logged out. Please save your work."
    );
    setShowLogout(true);
  });

  // Dismiss warning
  const handleDismissWarning = useCallback(() => {
    setShowWarning(false);
    setWarningMessage("");
  }, []);

  // Handle logout complete
  const handleLogoutComplete = useCallback(() => {
    console.debug("[GlobalNotificationHandler] Logout completed");
  }, []);

  return (
    <>
      {/* Toast Notifications - render only the most recent one */}
      {toasts.length > 0 && (
        <Toast
          key={toasts[toasts.length - 1].id}
          title="Incoming Administrator Message"
          message={`: ${toasts[toasts.length - 1].message}`}
          type={
            toasts[toasts.length - 1].type === "info"
              ? "global-notification"
              : toasts[toasts.length - 1].type
          }
          onDismiss={() => removeToast(toasts[toasts.length - 1].id)}
          autoDismiss={true}
          duration={10000}
          playSound={true}
        />
      )}

      {/* Warning Modal */}
      <MaintenanceWarning
        open={showWarning}
        onDismiss={handleDismissWarning}
        message={warningMessage}
      />

      {/* Force Logout Modal */}
      <ForceLogoutModal
        open={showLogout}
        message={logoutMessage}
        countdownSeconds={10}
        onLogoutComplete={handleLogoutComplete}
      />
    </>
  );
};

export default GlobalNotificationHandler;
